import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Loader2, Tag, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const TimesheetCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#6B7280' });
  const [submitting, setSubmitting] = useState(false);

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchCategories(); }, [currentOffice]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/categories`, { headers });
      if (res.ok) setCategories(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const url = editCat ? `${API_URL}/api/timesheets/categories/${editCat.id}` : `${API_URL}/api/timesheets/categories`;
      const res = await fetch(url, {
        method: editCat ? 'PUT' : 'POST', headers, body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(editCat ? 'Category updated' : 'Category created');
        setShowModal(false);
        setEditCat(null);
        setFormData({ name: '', description: '', color: '#6B7280' });
        fetchCategories();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch (err) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const res = await fetch(`${API_URL}/api/timesheets/categories/${id}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (res.ok) { toast.success('Category deleted'); fetchCategories(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (err) { toast.error('Failed'); }
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setFormData({ name: cat.name, description: cat.description || '', color: cat.color || '#6B7280' });
    setShowModal(true);
  };

  const openCreate = () => {
    setEditCat(null);
    setFormData({ name: '', description: '', color: '#6B7280' });
    setShowModal(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="timesheet-categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timesheet Categories</h1>
          <p className="text-gray-500">Manage categories that employees use to classify their timesheet entries</p>
        </div>
        <Button onClick={openCreate} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-2" />Add Category</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(cat => (
          <Card key={cat.id} className="hover:shadow-md transition-shadow" data-testid={`category-${cat.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat.color + '20' }}>
                    <Tag className="h-5 w-5" style={{ color: cat.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                    {cat.description && <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                    <Edit className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                <span className="text-xs text-gray-400">{cat.color}</span>
                {cat.is_default && <Badge variant="outline" className="text-[10px] ml-auto">Default</Badge>}
                {!cat.is_active && <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto">Inactive</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md" data-testid="category-modal">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editCat ? 'Edit Category' : 'New Category'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Research" required data-testid="cat-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description" data-testid="cat-description-input" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Palette className="h-4 w-4" />Color</Label>
                <div className="flex items-center gap-3">
                  <input type="color" value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})}
                    className="h-10 w-14 rounded cursor-pointer border" data-testid="cat-color-input" />
                  <Input value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})}
                    className="flex-1 font-mono text-sm" />
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: formData.color }} />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="save-category-btn">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editCat ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetCategories;
